import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Plus, UserPlus, X } from 'lucide-react';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Button } from '@/Components/ui/button';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Components/ui/popover';
import { listSavedInterviewers } from './savedInterviewersApi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const titleCase = (token) =>
  token.length ? `${token[0].toUpperCase()}${token.slice(1).toLowerCase()}` : token;
const initialsFor = (entry) => {
  const source = (entry?.name || entry?.email || '?').trim();
  if (!source) return '?';
  const parts = source.split(/[.\s@_-]+/).filter(Boolean);
  const seed = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return seed.toUpperCase();
};
const defaultNameFromEmail = (email, fallback = 'Interviewer') => {
  const local = (email || '').split('@')[0] || '';
  return local
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(titleCase)
    .join(' ') || fallback;
};

/**
 * Multi-select picker for the interviewer panel.
 *
 * Selected interviewers render as chips above the picker. Each person is an
 * Interviewer by default and can optionally be marked Interviewer + Co-host.
 * The logged-in SearchJob.ai user is the scheduling host and is shown
 * separately in the meeting summary.
 *
 * Adding can happen two ways:
 *   1. Pick one from the saved-interviewers dropdown (sourced from
 *      /api/company/saved-interviewers)
 *   2. Type a free-text email; pressing Enter or clicking the inline "Add"
 *      action adds it as a one-off (it is NOT saved to the directory — that
 *      is managed separately in Company Settings)
 *
 * Backwards compatible: the parent only needs to read the first entry as the
 * primary interviewer and pass the rest as additional_interviewers in the
 * booking payload, including optional is_cohost.
 */
const COPY_BY_VARIANT = {
  default: {
    addButton: 'Add interviewer',
    emptyHint: 'No interviewers added yet. Click "Add interviewer" to pick or type an email.',
    loadingSaved: 'Loading saved interviewers…',
    noSaved: 'No saved interviewers yet. Add some in Company Settings → Saved Interviewers so they show up here.',
    oneOffInvalid: 'Type a valid email above to add a one-off interviewer.',
    interviewerRole: 'Interviewer',
    cohostRole: 'Interviewer + Co-host',
    defaultName: 'Interviewer',
  },
  instant: {
    addButton: 'Add participant',
    emptyHint: 'No participants added yet. Click "Add participant" to pick or type an email.',
    loadingSaved: 'Loading saved participants…',
    noSaved: 'No saved participants yet. Add emails in Company Settings → Saved Interviewers or type one below.',
    oneOffInvalid: 'Type a valid email above to add a one-off participant.',
    interviewerRole: 'Interviewer',
    cohostRole: 'Interviewer + Co-host',
    defaultName: 'Participant',
  },
};

const InterviewerPanelPicker = ({
  value = [],
  onChange,
  candidateEmail = '',
  disabled = false,
  label = 'Interviewer panel',
  helperText = 'Pick one or more interviewers. Each receives a personal join link by email. Optionally mark someone as Co-host. You (logged in) are the scheduling host.',
  variant = 'default',
}) => {
  const copy = COPY_BY_VARIANT[variant] || COPY_BY_VARIANT.default;
  const [savedItems, setSavedItems] = useState([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [savedLoadError, setSavedLoadError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const normalizedCandidate = useMemo(
    () => normalizeEmail(candidateEmail),
    [candidateEmail]
  );

  const loadSaved = useCallback(async () => {
    setIsLoadingSaved(true);
    setSavedLoadError('');
    try {
      const rows = await listSavedInterviewers();
      if (mountedRef.current) setSavedItems(rows);
    } catch (err) {
      // Loading failures are not fatal — free-text entry still works.
      const msg = err?.response?.data?.error || err.message || 'Could not load saved interviewers.';
      if (mountedRef.current) setSavedLoadError(msg);
    } finally {
      if (mountedRef.current) setIsLoadingSaved(false);
    }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const selectedEmails = useMemo(
    () => new Set((value || []).map((entry) => normalizeEmail(entry?.email))),
    [value]
  );

  const trimmedQuery = query.trim();
  const queryEmail = normalizeEmail(trimmedQuery);
  const queryIsEmail = EMAIL_REGEX.test(queryEmail);

  const filteredSaved = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    return savedItems
      .filter((entry) => {
        const email = normalizeEmail(entry.interviewer_email);
        if (!email) return false;
        if (selectedEmails.has(email)) return false;
        if (!q) return true;
        return (
          email.includes(q)
          || (entry.interviewer_name || '').toLowerCase().includes(q)
          || (entry.job_title || '').toLowerCase().includes(q)
        );
      })
      .slice(0, 8);
  }, [savedItems, selectedEmails, trimmedQuery]);

  const addEntry = useCallback((entry, { keepOpen = false } = {}) => {
    const email = normalizeEmail(entry?.email);
    if (!email) return false;
    if (!EMAIL_REGEX.test(email)) return false;
    if (selectedEmails.has(email)) return false;
    if (normalizedCandidate && email === normalizedCandidate) return false;
    const next = [
      ...(value || []),
      {
        email,
        name: (entry?.name || defaultNameFromEmail(email, copy.defaultName)).trim(),
        source: entry?.source || 'free_text',
        is_cohost: false,
      },
    ];
    onChange?.(next);
    setQuery('');
    if (!keepOpen) setPickerOpen(false);
    // Re-focus the input when the popover stays open so the user can add
    // multiple entries quickly without grabbing the mouse again.
    if (keepOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    return true;
  }, [value, onChange, selectedEmails, normalizedCandidate]);

  const handleRemove = useCallback((email) => {
    const target = normalizeEmail(email);
    if (!target) return;
    const next = (value || []).filter((entry) => normalizeEmail(entry?.email) !== target);
    onChange?.(next);
  }, [value, onChange]);

  const toggleCohost = useCallback((email) => {
    const target = normalizeEmail(email);
    if (!target) return;
    const next = (value || []).map((entry) => (
      normalizeEmail(entry?.email) === target
        ? { ...entry, is_cohost: !entry?.is_cohost }
        : entry
    ));
    onChange?.(next);
  }, [value, onChange]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (queryIsEmail) {
        addEntry({ email: queryEmail, source: 'free_text' }, { keepOpen: true });
        return;
      }
      if (filteredSaved.length > 0) {
        const first = filteredSaved[0];
        addEntry({
          email: first.interviewer_email,
          name: first.interviewer_name,
          source: 'saved',
        }, { keepOpen: true });
      }
    } else if (event.key === 'Escape') {
      setPickerOpen(false);
    }
  };

  const isQueryAlreadySelected = queryIsEmail && selectedEmails.has(queryEmail);
  const isQueryCandidate = queryIsEmail && normalizedCandidate && queryEmail === normalizedCandidate;

  const canAddQuery = queryIsEmail && !isQueryAlreadySelected && !isQueryCandidate;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-xs text-slate-500">
          {value?.length ? `${value.length} selected` : 'None selected'}
        </span>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-2">
        {value && value.length > 0 ? (
          <ul className="flex flex-wrap gap-2 p-1">
            {value.map((entry, index) => {
              const email = normalizeEmail(entry?.email);
              const name = (entry?.name || '').trim() || defaultNameFromEmail(email, copy.defaultName);
              const isPrimary = index === 0;
              const isCohost = Boolean(entry?.is_cohost);
              return (
                <li
                  key={email}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium',
                    isPrimary
                      ? 'border-sky-200 bg-sky-50 text-sky-900'
                      : 'border-slate-200 bg-slate-50 text-slate-800'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold',
                      isPrimary ? 'bg-sky-200 text-sky-900' : 'bg-slate-200 text-slate-800'
                    )}
                  >
                    {initialsFor({ name, email })}
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="font-semibold">{name}</span>
                    <span className="text-[11px] text-slate-500 font-normal">{email}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleCohost(email)}
                    disabled={disabled}
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition',
                      isCohost
                        ? 'border-violet-200 bg-violet-50 text-violet-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:text-violet-700',
                      disabled && 'cursor-not-allowed opacity-50'
                    )}
                    aria-pressed={isCohost}
                    title={isCohost ? 'Remove Co-host role' : 'Make Co-host'}
                  >
                    {isCohost ? copy.cohostRole : copy.interviewerRole}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(email)}
                    disabled={disabled}
                    className={cn(
                      'rounded-full p-0.5 text-slate-500 transition hover:bg-white hover:text-red-600',
                      disabled && 'cursor-not-allowed opacity-50'
                    )}
                    aria-label={`Remove ${email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-2 py-2 text-xs text-slate-500">
            {copy.emptyHint}
          </div>
        )}

        <div className="border-t border-slate-100 mt-2 pt-2">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                className="text-sky-700 hover:bg-sky-50 hover:text-sky-800"
              >
                <Plus className="mr-1 h-4 w-4" />
                {copy.addButton}
                <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <div className="border-b border-slate-100 p-2">
                <Input
                  ref={inputRef}
                  type="email"
                  autoFocus
                  placeholder="Search saved or type a new email"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-9"
                />
              </div>

              <div className="max-h-72 overflow-y-auto py-1">
                {isLoadingSaved ? (
                  <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {copy.loadingSaved}
                  </div>
                ) : null}

                {!isLoadingSaved && savedLoadError ? (
                  <div className="px-3 py-2 text-xs text-red-600">
                    {savedLoadError}
                  </div>
                ) : null}

                {!isLoadingSaved && savedItems.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-slate-500">
                    {copy.noSaved}
                  </div>
                ) : null}

                {!isLoadingSaved && filteredSaved.length > 0 ? (
                  <div className="px-2">
                    <div className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      From your team
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {filteredSaved.map((entry) => (
                        <li key={entry.interviewer_email}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                            onClick={() =>
                              addEntry({
                                email: entry.interviewer_email,
                                name: entry.interviewer_name,
                                source: 'saved',
                              }, { keepOpen: true })
                            }
                          >
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700">
                              {initialsFor({ name: entry.interviewer_name, email: entry.interviewer_email })}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-slate-900">
                                {entry.interviewer_name || entry.interviewer_email}
                              </span>
                              <span className="block truncate text-xs text-slate-500">
                                {entry.interviewer_email}
                                {entry.job_title ? <span> · {entry.job_title}</span> : null}
                              </span>
                            </span>
                            <Check className="h-4 w-4 text-transparent" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="border-t border-slate-100 mt-2 px-2 py-2">
                  <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    One-off
                  </div>
                  {canAddQuery ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md bg-sky-50 px-2 py-1.5 text-left text-sm text-sky-900 hover:bg-sky-100"
                      onClick={() => addEntry({ email: queryEmail, source: 'free_text' }, { keepOpen: true })}
                    >
                      <UserPlus className="h-4 w-4 text-sky-700" />
                      <span>
                        Add <span className="font-semibold">{queryEmail}</span>
                      </span>
                    </button>
                  ) : (
                    <div className="px-2 py-1 text-xs text-slate-500">
                      {isQueryAlreadySelected
                        ? `${queryEmail} is already on the panel.`
                        : isQueryCandidate
                          ? 'This is the candidate email — pick a different participant.'
                          : copy.oneOffInvalid}
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <p className="text-xs text-slate-500">{helperText}</p>
    </div>
  );
};

export default InterviewerPanelPicker;
